import { useLocation, Link, Outlet } from 'react-router-dom';
import {
  ChartBarIcon,
  UsersIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { usePanelResize } from '@/hooks/usePanelResize';
import { UserRole } from '@/types/auth';
import { useTranslation } from 'react-i18next';
import { PanelResizeHandle } from '@/components/PanelResizeHandle';

const navigation = [
  { nameKey: 'admin.nav.overview', href: '/admin', icon: ChartBarIcon },
  { nameKey: 'admin.nav.users', href: '/admin/users', icon: UsersIcon },
  { nameKey: 'admin.nav.activities', href: '/admin/activities', icon: ClockIcon },
] as const;

export function AdminLayout() {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === UserRole.ADMIN;
  const sidebar = usePanelResize({
    label: t('panels.resizeAdmin'),
    storageKey: 'sak.panels.adminSidebarWidth',
    defaultWidth: 256,
    minWidth: 200,
    maxWidth: 400,
    dock: 'left',
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen text-muted-foreground">
        {t('admin.requiresAdmin')}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="flex">
        <div
          className="hidden flex-shrink-0 flex-col md:flex"
          style={{ width: sidebar.width }}
        >
          <div className="flex min-h-0 flex-1 flex-col bg-card">
            <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
              <div className="flex flex-shrink-0 items-center px-4">
                <h1 className="text-lg font-semibold text-foreground">{t('admin.panel')}</h1>
              </div>
              <nav className="mt-5 flex-1 space-y-1 bg-card px-2">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-active text-accent'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <item.icon
                        className={`mr-3 h-6 w-6 flex-shrink-0 ${
                          isActive ? 'text-muted-foreground' : 'text-muted-foreground group-hover:text-muted-foreground'
                        }`}
                        aria-hidden="true"
                      />
                      {t(item.nameKey)}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
        <PanelResizeHandle
          state={sidebar}
          variant="separator"
          className="hidden md:block"
        />

        <div className="flex flex-1 flex-col">
          <main className="flex-1" data-suite-selectable>
            <div className="py-6">
              <div className="w-full px-4 sm:px-6 md:px-8">
                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
